/**
 * The Watching cell of both homescreen tables (SPEC `New Post Watching` § Homescreen).
 *
 * A compact switch, independent of the schedule column and written immediately (#70), exactly like
 * the Mode cell: an editor cell hosts its own control and drops the row's `.cell-linked` overlay,
 * so clicking it toggles instead of navigating.
 *
 * Two states, two sources. `enabled` is the row's **own** target row — what the switch writes back,
 * and the only thing it may show as "on" (a row watched only through a group has nothing of its own
 * to turn off). `effectiveOn` folds in the group union, and paints the quieter "on via something
 * else" face. Why either is the way it is lives in the server's one-sentence `hoverDisplay`, which
 * is rendered verbatim as the title (§12.5) — the client never assembles that sentence.
 */

import type { WatchingBlockDTO } from '@lw/shared';
import { cx } from '../../components';

export type WatchToggleProps = {
  watching: WatchingBlockDTO;
  /** Profile or group name — the accessible label of an otherwise wordless control. */
  subjectLabel: string;
  /** Archived rows: the control is inert, per SPEC. */
  disabled?: boolean;
  /**
   * True while **this row's** #70 write is in flight — scoped to the clicked row, so a slow write
   * never freezes the rest of the column. The switch itself has already moved (the write is
   * optimistic); this only blocks a second click on the same row until the answer lands.
   */
  pending?: boolean;
  onToggle: (enabled: boolean) => void;
};

export function WatchToggle({
  watching,
  subjectLabel,
  disabled = false,
  pending = false,
  onToggle,
}: WatchToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={watching.enabled}
      aria-label={`Watching for ${subjectLabel}`}
      title={watching.hoverDisplay}
      className={cx(
        'cell-edit watch-switch',
        watching.enabled && 'watch-switch--on',
        !watching.enabled && watching.effectiveOn && 'watch-switch--inherited',
      )}
      disabled={disabled || pending}
      onClick={() => onToggle(!watching.enabled)}
    >
      <span className="watch-switch__track" aria-hidden="true">
        <span className="watch-switch__knob" />
      </span>
    </button>
  );
}

export default WatchToggle;
